import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import './AnswerSheet.css';

/**
 * Componente de hoja de respuestas estilo ICFES Saber 11
 * Muestra óvalos (burbujas) para marcar respuestas A, B, C, D
 */
const AnswerSheet = ({ 
  questionNumber, 
  selectedAnswer, 
  onAnswerSelect, 
  showResult = false,
  correctAnswer = null,
  disabled = false 
}) => {
  const options = ['A', 'B', 'C', 'D'];

  const getOvalClass = (option) => {
    const classes = ['answer-oval'];
    
    if (disabled) {
      classes.push('disabled');
    }

    if (selectedAnswer === option) {
      classes.push('selected');
      
      // Si estamos mostrando resultados
      if (showResult) {
        if (option === correctAnswer) {
          classes.push('correct');
        } else {
          classes.push('incorrect');
        }
      }
    }

    // Mostrar la respuesta correcta si está mal
    if (showResult && option === correctAnswer && selectedAnswer !== correctAnswer) {
      classes.push('correct-answer');
    }

    return classes.join(' ');
  };

  return (
    <Box className="answer-sheet-container">
      <Box className="question-number-box">
        <Typography variant="body2" className="question-number">
          {questionNumber}
        </Typography>
      </Box>
      
      <Box className="ovals-container">
        {options.map((option) => (
          <Box
            key={option}
            className={getOvalClass(option)}
            onClick={() => !disabled && onAnswerSelect(option)}
            role="button"
            tabIndex={0}
            aria-label={`Opción ${option}`}
            onKeyPress={(e) => {
              if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                onAnswerSelect(option);
              }
            }}
          >
            <Typography variant="body2" className="oval-letter">
              {option}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Componente de cuadernillo completo con múltiples preguntas
 * Simula la hoja de respuestas física del ICFES
 */
export const AnswerSheetGrid = ({ 
  totalQuestions, 
  answers, 
  onAnswerSelect, 
  currentQuestion,
  onQuestionNavigate,
  showResults = false,
  correctAnswers = {},
  disabled = false 
}) => {
  return (
    <Paper elevation={3} className="answer-sheet-paper">
      <Box className="answer-sheet-header">
        <Typography variant="h6" className="sheet-title">
          HOJA DE RESPUESTAS
        </Typography>
        <Typography variant="caption" className="sheet-subtitle">
          Marque solo una opción por pregunta
        </Typography>
      </Box>

      <Box className="answer-sheet-grid">
        {Array.from({ length: totalQuestions }, (_, index) => {
          const questionNum = index + 1;
          const isActive = currentQuestion === index;
          
          return (
            <Box 
              key={questionNum}
              className={`grid-item ${isActive ? 'active' : ''}`}
              onClick={() => onQuestionNavigate && onQuestionNavigate(index)}
            >
              <AnswerSheet
                questionNumber={questionNum}
                selectedAnswer={answers[index]}
                onAnswerSelect={(answer) => onAnswerSelect(index, answer)}
                showResult={showResults}
                correctAnswer={correctAnswers[index]}
                disabled={disabled}
              />
            </Box>
          );
        })}
      </Box>

      <Box className="answer-sheet-footer">
        <Box className="legend-item">
          <Box className="legend-oval selected"></Box>
          <Typography variant="caption">Respuesta marcada</Typography>
        </Box>
        <Box className="legend-item">
          <Box className="legend-oval"></Box>
          <Typography variant="caption">Sin marcar</Typography>
        </Box>
        {showResults && (
          <>
            <Box className="legend-item">
              <Box className="legend-oval correct"></Box>
              <Typography variant="caption">Correcta</Typography>
            </Box>
            <Box className="legend-item">
              <Box className="legend-oval incorrect"></Box>
              <Typography variant="caption">Incorrecta</Typography>
            </Box>
          </>
        )}
      </Box>
    </Paper>
  );
};

export default AnswerSheet;
